import { trpc } from '@tencent/tpro_rmonitor_product_manager_product_user';
import { get } from 'lodash-es';
import moment from 'moment';
import { TrpcRequestConfig } from '@/infrastructure/axios';
import { BusiCode } from '@/infrastructure/axios/code';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { LoginType } from '@/presentation/hooks/user/types';
import { enumTrpc } from '@/proto/generated/tpro_rmonitor_product_manager_product_user';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';
import IGetUserListReq = trpc.rmonitor.product_manager.IGetUserListReq;
import IDeleteUserListReq = trpc.rmonitor.product_manager.IDeleteUserListReq;
import ISetUserListReq = trpc.rmonitor.product_manager.ISetUserListReq;
import ISetUserListRsp = trpc.rmonitor.product_manager.ISetUserListRsp;
export type IProductUserInfo = trpc.rmonitor.product_manager.IProductUserInfo;
export type ISetRemarkRsp = WithTypeCast<trpc.rmonitor.product_manager.ISetRemarkRsp>;
export type  ISetRemarkReq = WithTypeCast<trpc.rmonitor.product_manager.ISetRemarkReq>;
export type  ICheckUserExistReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckUserExistReq>;
export type ICheckUserExistRsp = WithTypeCast<trpc.rmonitor.product_manager.ICheckUserExistRsp>;
export type ISetExpireStrategiesReq = WithTypeCast<trpc.rmonitor.product_manager.ISetExpireStrategiesReq>;
export type ISetExpireStrategiesRsp = WithTypeCast<trpc.rmonitor.product_manager.ISetExpireStrategiesRsp>;
export type IChangeExpireTimeReq = WithTypeCast<trpc.rmonitor.product_manager.IChangeExpireTimeReq>;
export type IChangeExpireTimeRsp = WithTypeCast<trpc.rmonitor.product_manager.IChangeExpireTimeRsp>;
export type IGetExpireStrategiesReq = WithTypeCast<trpc.rmonitor.product_manager.IGetExpireStrategiesReq>;
export type IGetExpireStrategiesRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetExpireStrategiesRsp>;
export type ExpireStrategy = WithTypeCast<enumTrpc.rmonitor.product_manager.ExpireStrategy>;
export const { ExpireStrategy } = enumTrpc.rmonitor.product_manager;
export type ICreateInvitationReq = WithTypeCast<trpc.rmonitor.product_manager.ICreateInvitationReq>;
export type ICreateInvitationRsp = WithTypeCast<trpc.rmonitor.product_manager.ICreateInvitationRsp>;
export type IGetInvitationListReq = WithTypeCast<trpc.rmonitor.product_manager.IGetInvitationListReq>;
export type IGetInvitationListRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetInvitationListRsp>;
export type IInvitationItem = WithTypeCast<trpc.rmonitor.product_manager.IInvitationItem>;
export type IJoinedMember = WithTypeCast<trpc.rmonitor.product_manager.IJoinedMember>;
export type IGetInvitationDetailReq = WithTypeCast<trpc.rmonitor.product_manager.IGetInvitationDetailReq>;
export type IGetInvitationDetailRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetInvitationDetailRsp>;
export type IJoinByInvitationReq = WithTypeCast<trpc.rmonitor.product_manager.IJoinByInvitationReq>;
export type IJoinByInvitationRsp = WithTypeCast<trpc.rmonitor.product_manager.IJoinByInvitationRsp>;


export interface ProductMember {
  id: number;
  name: string;
  buglyId: string;
  thirdAccountType: LoginType;
  thirdAccountId: string;
  department?: string;
  mail: string;
  phone: string;

}

/**
 * 产品成员管理
 */
export class ProductUserService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.ProductUserService,
    });
  }

  /**
   * 添加成员
   */
  public async add(req: ISetUserListReq): Promise<ISetUserListRsp> {
    try {
      return await this.trpcPost('SetUserList', req);
    } catch (e: any) {
      return {
        baseRsp: {
          code: e.message !== null ? Number(e.message) : e.message,
        },
      };
    }
  }

  /**
   * 删除成员
   */
  public async remove(req: IDeleteUserListReq): Promise<boolean> {
    try {
      await this.trpcPost('DeleteUserList', req);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 修改成员
   * 一般用户修改成员角色
   */
  public async update(req: ISetUserListReq, config?: any): Promise<ISetUserListRsp> {
    try {
      return await this.trpcPost('SetUserList', req, config);
    } catch (e: any) {
      return {
        baseRsp: {
          code: e.message !== null ? Number(e.message) : e.message,
        },
      };
    }
  }

  /**
   * 获取产品成员列表
   */
  public async getAll(req: IGetUserListReq): Promise<IProductUserInfo[]> {
    try {
      const rsp = await this.trpcPost('GetUserList', req);
      return get(rsp, 'productUsers', []);
    } catch (e) {
      return [];
    }
  }

  /**
   * 更新用户备注
   */
  public updateRemark = async (req: ISetRemarkReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost<ISetRemarkRsp>('UpdateRemark', req);
      return res?.baseRsp?.code  === BusiCode.Ok;
    } catch (e) {
      return false;
    }
  };

  // CheckUserExist （批量）检查用户是否在用户列表内
  public checkUserExist = async (req: ICheckUserExistReq) => {
    try {
      const resp: ICheckUserExistRsp = await this.trpcPost('CheckUserExist', req);
      const data = resp.productUsers ?? [];
      return data.map((item: IProductUserInfo) => ({
        buglyId: item.buglyId,
        roleId: item.roleId,
        roleName: item.roleName,
        expireTime: item.expireTime ? moment(item.expireTime, 'X').format('YYYY-MM-DD') : '',
      }));
    } catch (e) {
      return [];
    }
  };

  // 设置续期策略
  public setExpireStrategies = async (req: ISetExpireStrategiesReq): Promise<boolean> => {
    try {
      const resp  = await this.trpcPost<ISetExpireStrategiesRsp>('SetExpireStrategies', req);

      return resp?.baseRsp?.code === BusiCode.Ok;
    } catch (e) {
      return false;
    }
  };

  // 续期
  public changeExpireTime = async (req: IChangeExpireTimeReq): Promise<boolean> => {
    try {
      const resp  = await this.trpcPost<IChangeExpireTimeRsp>('ChangeExpireTime', req);

      return resp?.baseRsp?.code === BusiCode.Ok;
    } catch (e) {
      return false;
    }
  };

  // 获取续期策略
  public getExpireStrategies = async (req: IGetExpireStrategiesReq): Promise<ExpireStrategy[]> => {
    try {
      const resp  = await this.trpcPost<IGetExpireStrategiesRsp>('GetExpireStrategies', req);
      if (!Array.isArray(resp?.Strategies)) {
        return [];
      }
      return resp?.Strategies ?? [];
    } catch (e) {
      return [];
    }
  };

  /**
   * 创建邀请链接
   */
  public createInvitation = async (req: ICreateInvitationReq): Promise<ICreateInvitationRsp> => {
    try {
      return await this.trpcPost<ICreateInvitationRsp>('CreateInvitation', req);
    } catch (e) {
      return {};
    }
  };

  /**
   * 获取邀请列表
   */
  public getInvitationList = async (req: IGetInvitationListReq): Promise<IGetInvitationListRsp> => {
    try {
      return await this.trpcPost<IGetInvitationListRsp>('GetInvitationList', req);
    } catch (e) {
      return { items: [] };
    }
  };

  /**
   * 获取邀请详情（加入产品页使用）
   */
  public getInvitationDetail = async (req: IGetInvitationDetailReq): Promise<IGetInvitationDetailRsp> => {
    try {
      return await this.trpcPost<IGetInvitationDetailRsp>('GetInvitationDetail', req);
    } catch (e) {
      return {};
    }
  };

  /**
   * 通过邀请链接加入产品
   * @param config 可透传 headers（如 X-ProductID）；productId 由 token 经 GetInvitationDetail 反查得到后显式指定
   */
  public joinByInvitation = async (
    req: IJoinByInvitationReq,
    config?: TrpcRequestConfig,
  ): Promise<IJoinByInvitationRsp> => {
    try {
      return await this.trpcPost<IJoinByInvitationRsp>('JoinByInvitation', req, config);
    } catch (e) {
      return {};
    }
  };
}

export const productUserService = new ProductUserService();
